/**
 * The monthly pack's «Salute» section — the Health figures frozen at the month's close (bata-reqs docs/25 §6,
 * §5.4 B3; bata-platform ADR 9016 as amended 2026-11-01, ADR 9053/9054; `docs/adr/3002` amended).
 *
 * The pack is shown exactly as the platform froze it: one row per frozen figure — the four reports at
 * market and department-family level, and the queues' undecided counts at the close (`report: pending`) —
 * with the target in force at the close and its change set. Nothing is recomputed, coloured or totalled
 * here: a frozen row is a fact about the close. A figure the platform froze without a source carries its
 * reason, in words. A pack frozen before the section existed has no `health` (`null`): the page says so,
 * never an empty table.
 */

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { useFamilyLabel } from '@/api';
import type { MonthlyReport, MonthlyReportHealthRow } from '@/api/generated';
import type { ExportCell, ExportColumn } from '@/export';
import { formatDate, formatDateTime } from '@/lib/format';
import { DataTable } from '@/planning';

import { absentWords, formatFigure } from './health/figures';
import { PENDING_WORDS, UNKNOWN_PENDING_KEY, measureWords } from './health/reports';

/** Each frozen report's words. */
export const PACK_HEALTH_REPORT_KEY: Readonly<Record<string, string>> = {
  availability: 'strategic.health.availability.title',
  coverage: 'strategic.health.coverage.title',
  sell_through: 'strategic.health.sellThrough.title',
  markdown_exposure: 'strategic.health.markdown.title',
  pending: 'strategic.pack.health.pending',
};

/** A frozen row's figure in words: a pending row names its queue, any other its measure. Pure. */
export function packHealthMeasureKey(row: Pick<MonthlyReportHealthRow, 'report' | 'measure'>): string {
  if (row.report === 'pending') return PENDING_WORDS[row.measure] ?? UNKNOWN_PENDING_KEY;
  return measureWords(row.measure).labelKey;
}

/** Whether the pack froze a Health section: `false` for a pack frozen before it existed. Pure. */
export function packHasHealth(report: Pick<MonthlyReport, 'health'>): boolean {
  return report.health !== null && report.health !== undefined;
}

function packValue(t: TFunction, row: MonthlyReportHealthRow, locale: string): string {
  const shown = formatFigure({ value: row.value, unit: row.unit, currencyCode: null }, locale);
  return shown ?? absentWords(t, row);
}

export function PackHealth({ report }: { readonly report: MonthlyReport }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const familyLabel = useFamilyLabel();
  if (!packHasHealth(report)) {
    return (
      <p className="text-caption text-ink-secondary" data-pack-health="not-captured">
        {t('strategic.pack.health.notCaptured', { when: formatDateTime(report.frozenAt, locale, 'UTC') })}
      </p>
    );
  }
  const rows = report.health ?? [];
  const group = (row: MonthlyReportHealthRow): string => (row.level === 'market' || row.groupKey === null
    ? t('strategic.pack.health.market', { market: report.countryKey })
    : row.groupKey === '-' ? t('strategic.health.group.noFamily') : familyLabel(row.groupKey));
  return (
    <div className="flex flex-col gap-2" data-pack-health={rows.length}>
      <p className="text-caption text-ink-secondary">{t('strategic.pack.health.lead')}</p>
      <DataTable
        sourceKey="strategic.help.monthlyPackScreen.sourceHealth"
        helpValues={{ market: report.countryKey, frozen: formatDateTime(report.frozenAt, locale, 'UTC') }}
        rows={rows}
        rowKey={(row) => `${row.report}|${row.measure}|${row.level}|${row.groupKey ?? ''}`}
        captionKey="strategic.pack.health.caption"
        exportId="strategic.pack.health"
        exportTitleKey="strategic.pack.health.title"
        cap={false}
        density
        rowProps={() => ({ className: 'border-b border-hairline align-top' })}
        columns={[
          {
            id: 'report', headerKey: 'strategic.pack.health.column.report', descriptionKey: 'strategic.help.monthlyPackScreen.colHealthReport', kind: 'enum', headerClassName: 'py-1',
            value: (row) => row.report, label: (code) => t(PACK_HEALTH_REPORT_KEY[code] ?? 'strategic.pack.health.reportUnknown', { code }),
            cell: (row) => <th scope="row" className="py-1 text-start font-normal">{t(PACK_HEALTH_REPORT_KEY[row.report] ?? 'strategic.pack.health.reportUnknown', { code: row.report })}</th>,
          },
          {
            id: 'measure', headerKey: 'strategic.pack.health.column.measure', descriptionKey: 'strategic.help.monthlyPackScreen.colHealthMeasure', kind: 'text', headerClassName: 'py-1',
            value: (row) => t(packHealthMeasureKey(row), { source: row.measure }),
            cell: (row) => <td className="py-1">{t(packHealthMeasureKey(row), { source: row.measure })}</td>,
          },
          {
            id: 'group', headerKey: 'strategic.pack.health.column.group', descriptionKey: 'strategic.help.monthlyPackScreen.colHealthGroup', kind: 'text', headerClassName: 'py-1',
            value: (row) => group(row), cell: (row) => <td className="py-1">{group(row)}</td>,
          },
          {
            id: 'value', headerKey: 'strategic.pack.column.value', descriptionKey: 'strategic.help.monthlyPackScreen.colHealthValue', kind: 'number', align: 'end', headerClassName: 'py-1',
            value: (row) => row.value,
            cell: (row) => <td className="font-numeric py-1 text-end tabular-nums" data-health-reason={row.absentReason ?? undefined}>{packValue(t, row, locale)}</td>,
          },
          {
            id: 'target', headerKey: 'strategic.pack.health.column.target', descriptionKey: 'strategic.help.monthlyPackScreen.colHealthTarget', kind: 'number', align: 'end', headerClassName: 'py-1',
            value: (row) => row.target,
            cell: (row) => (
              <td className="font-numeric py-1 text-end tabular-nums">
                {row.target === null ? t('strategic.pack.health.noTarget') : formatFigure({ value: row.target, unit: row.unit, currencyCode: null }, locale)}
              </td>
            ),
          },
          {
            id: 'source', headerKey: 'strategic.pack.health.column.source', descriptionKey: 'strategic.help.monthlyPackScreen.colHealthSource', kind: 'text', headerClassName: 'py-1',
            value: (row) => row.sourceRunId ?? '',
            cell: (row) => (
              <td className="py-1 text-caption">
                {row.sourceRunId === null ? t('absent.notStated') : <span translate="no" className="font-numeric">{row.sourceRunId}</span>}
                {row.proxy && <span className="text-ink-secondary block">{t('strategic.pack.health.proxy')}</span>}
              </td>
            ),
          },
          {
            id: 'as_of', headerKey: 'strategic.pack.health.column.asOf', descriptionKey: 'strategic.help.monthlyPackScreen.colHealthAsOf', kind: 'date', headerClassName: 'py-1',
            value: (row) => row.asOf,
            cell: (row) => <td className="font-numeric py-1">{row.asOf === null ? t('absent.notStated') : formatDate(row.asOf, locale, 'UTC')}</td>,
          },
        ]}
      />
    </div>
  );
}

// ── the workbook sheet ───────────────────────────────────────────────────────

const missing: ExportCell = { kind: 'missing', state: 'not-reported' };
const textCell = (value: string | null): ExportCell => (value === null || value === '' ? missing : { kind: 'text', value });
const codeCell = (value: string | null): ExportCell => (value === null || value === '' ? missing : { kind: 'label', value, code: value });
const numberCell = (value: number | null): ExportCell => (value === null ? missing : { kind: 'number', raw: value });

/** The «Salute» sheet's columns: the frozen row, verbatim. */
export const PACK_HEALTH_COLUMNS: readonly ExportColumn[] = [
  { key: 'report', headerKey: 'strategic.pack.health.column.report', kind: 'label', descriptionKey: 'strategic.columnGuide.pack.healthReport' },
  { key: 'measure', headerKey: 'strategic.pack.health.column.measure', kind: 'label', descriptionKey: 'strategic.columnGuide.pack.healthMeasure' },
  { key: 'level', headerKey: 'strategic.pack.health.column.level', kind: 'label', descriptionKey: 'strategic.columnGuide.pack.healthLevel' },
  { key: 'group_key', headerKey: 'strategic.pack.health.column.group', kind: 'text', descriptionKey: 'strategic.columnGuide.pack.healthGroup' },
  { key: 'value', headerKey: 'strategic.pack.column.value', kind: 'number', descriptionKey: 'strategic.columnGuide.pack.healthValue' },
  { key: 'numerator', headerKey: 'strategic.pack.health.column.numerator', kind: 'number', descriptionKey: 'strategic.columnGuide.pack.healthNumerator' },
  { key: 'denominator', headerKey: 'strategic.pack.health.column.denominator', kind: 'number', descriptionKey: 'strategic.columnGuide.pack.healthDenominator' },
  { key: 'unit', headerKey: 'strategic.pack.column.unit', kind: 'label', descriptionKey: 'strategic.columnGuide.pack.healthUnit' },
  { key: 'target', headerKey: 'strategic.pack.health.column.target', kind: 'number', descriptionKey: 'strategic.columnGuide.pack.healthTarget' },
  { key: 'target_change_id', headerKey: 'strategic.pack.health.column.targetChangeId', kind: 'text', descriptionKey: 'strategic.columnGuide.pack.healthTargetChangeId' },
  { key: 'source_run_id', headerKey: 'strategic.pack.health.column.source', kind: 'text', descriptionKey: 'strategic.columnGuide.pack.healthSource' },
  { key: 'as_of', headerKey: 'strategic.pack.health.column.asOf', kind: 'date', descriptionKey: 'strategic.columnGuide.pack.healthAsOf' },
  { key: 'period_key', headerKey: 'strategic.pack.health.column.period', kind: 'text', descriptionKey: 'strategic.columnGuide.pack.healthPeriod' },
  { key: 'proxy', headerKey: 'strategic.pack.health.column.proxy', kind: 'label', descriptionKey: 'strategic.columnGuide.pack.healthProxy' },
  { key: 'absent_reason', headerKey: 'strategic.pack.health.column.reason', kind: 'label', descriptionKey: 'strategic.columnGuide.pack.healthReason' },
];

/** One frozen row as the sheet's cells, in `PACK_HEALTH_COLUMNS` order. Pure. */
export function packHealthCells(row: MonthlyReportHealthRow): ExportCell[] {
  return [
    codeCell(row.report), codeCell(row.measure), codeCell(row.level), textCell(row.groupKey),
    numberCell(row.value), numberCell(row.numerator), numberCell(row.denominator), codeCell(row.unit),
    numberCell(row.target), textCell(row.targetChangeId), textCell(row.sourceRunId),
    row.asOf === null ? missing : { kind: 'date', iso: row.asOf }, textCell(row.periodKey),
    codeCell(row.proxy ? 'proxy' : 'measured'), codeCell(row.absentReason),
  ];
}
